import React, { useState, useEffect } from "react";
import useSWR from 'swr';
import api from "../utils/api";
import { Bell, Calendar, User, Package, Zap, DollarSign, UserCheck, FileSignature } from "lucide-react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";

const NotificationBell = ({ onViewRequests }) => {
  const [open, setOpen] = useState(false);
  const [viewedActivities, setViewedActivities] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('viewedNotifications');
    if (stored) {
      try {
        return new Set(JSON.parse(stored));
      } catch (e) {
        return new Set();
      }
    }
    return new Set();
  });
  
  // SWR fetcher
  const fetcher = (url) => api.get(url).then(res => res.data);
  
  // Fetch recent activities with SWR (auto-refresh every 30s)
  const { data, error, isLoading } = useSWR(
    '/activity-log?limit=20',
    fetcher,
    {
      refreshInterval: 30000, // Auto-refresh every 30 seconds
      revalidateOnFocus: true, // Refresh when tab becomes active
      dedupingInterval: 5000 // Prevent duplicate requests within 5s
    }
  );
  
  const allActivities = data?.activities || [];
  
  // Filter to notification-worthy events
  const notificationEvents = allActivities.filter(activity => {
    const action = activity.action?.toLowerCase() || '';
    const resourceType = activity.resourceType?.toLowerCase() || '';
    
    // Notification-worthy events (6 types)
    return (
      (action === 'created' && resourceType === 'booking') ||         // New booking
      (action === 'payment_received' && resourceType === 'booking') || // Payment
      (action === 'captured' && resourceType === 'lead') ||            // Lead
      (action === 'checked_in' && resourceType === 'booking') ||       // Check-in
      (action === 'signed' && resourceType === 'waiver') ||            // Waiver
      (action === 'created' && resourceType === 'service')             // Service
    );
  });
  
  // Show max 10 in dropdown
  const recentNotifications = notificationEvents.slice(0, 10);
  
  // Count only UNVIEWED
  const unviewedCount = notificationEvents.filter(activity => {
    const id = activity.id || activity.timestamp;
    return id && !viewedActivities.has(id);
  }).length;
  
  // Cap badge at 5+
  const badgeCount = unviewedCount > 5 ? '5+' : unviewedCount;
  
  // Mark all as viewed when opened
  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    
    if (isOpen && recentNotifications.length > 0) {
      // Mark all current notifications as viewed
      const currentIds = recentNotifications.map(a => a.id || a.timestamp).filter(Boolean);
      const newViewed = new Set([...viewedActivities, ...currentIds]);
      setViewedActivities(newViewed);
      localStorage.setItem('viewedNotifications', JSON.stringify([...newViewed]));
    }
  };

  const getActivityIcon = (activity) => {
    const resource = activity.resourceType?.toLowerCase() || '';
    const action = activity.action?.toLowerCase() || '';
    
    if (action === 'payment_received') return DollarSign;
    if (action === 'captured' && resource === 'lead') return UserCheck;
    if (action === 'checked_in') return UserCheck;
    if (action === 'signed' && resource === 'waiver') return FileSignature;
    if (resource.includes('booking')) return Calendar;
    if (resource.includes('service')) return Zap;
    if (resource.includes('asset')) return Package;
    if (resource.includes('team')) return User;
    return Calendar;
  };

  const getActivityMessage = (activity) => {
    const action = activity.action;
    const resource = activity.resourceType;
    const name = activity.resourceName || 'Item';
    
    // Custom messages for each event type
    if (action === 'created' && resource === 'booking') {
      return `New booking: ${name}`;
    }
    if (action === 'payment_received') {
      const amount = activity.details?.amount ? `$${activity.details.amount}` : '';
      return `Payment received ${amount} - ${name}`;
    }
    if (action === 'captured' && resource === 'lead') {
      return `New lead: ${name}`;
    }
    if (action === 'checked_in') {
      return `${name} checked in`;
    }
    if (action === 'signed' && resource === 'waiver') {
      return `Waiver signed by ${name}`;
    }
    if (action === 'created' && resource === 'service') {
      return `New service: ${name}`;
    }
    
    // Fallback
    return `${action} ${resource}: ${name}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="notification-bell"
        >
          <Bell className="h-5 w-5" />
          {unviewedCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
            >
              {badgeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg">Notifications</h3>
            {unviewedCount > 0 && (
              <Badge variant="secondary">{unviewedCount} new</Badge>
            )}
          </div>
          
          {recentNotifications.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No recent notifications
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recentNotifications.map((activity, index) => {
                const ResourceIcon = getActivityIcon(activity);
                
                return (
                  <div 
                    key={activity.id || index} 
                    className="p-3 border rounded-lg hover:bg-accent transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <ResourceIcon className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">
                          {getActivityMessage(activity)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatTimestamp(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
